import { z } from 'zod';
import { isoDateTimeSchema, optional } from './common';

/*
 * Rotas que existem só no BFF: auth, me, usuarios, health, conta (senha e 2FA) e configuracoes.
 * Mesma convenção do restante do contrato: português, sem camelCase, hífen entre palavras.
 */

/** `importacao` (logística) e `financeiro` entram com a sessão "Importação em Andamento" (docs/09). */
export const perfilSchema = z.enum(['planner', 'viewer', 'operator', 'admin', 'importacao', 'financeiro']);
export type Perfil = z.infer<typeof perfilSchema>;
export const PERFIS: readonly Perfil[] = perfilSchema.options;

// ---------- Política de senha ----------

export const PASSWORD_MIN_LENGTH = 10;
export const PASSWORD_MIN_SPECIAL = 2;

/** Caractere especial = qualquer símbolo ou pontuação (nem letra, nem número, nem espaço). */
const SPECIAL = /[^\p{L}\p{N}\s]/gu;
const countSpecial = (s: string) => (s.match(SPECIAL) ?? []).length;

export interface PasswordRule {
  id: 'length' | 'upper' | 'digit' | 'special' | 'spaces';
  /** Texto exibido na tela e devolvido na validação */
  label: string;
  test: (senha: string) => boolean;
}

/**
 * Regras de TODA senha definida no sistema (troca da própria senha, senha provisória do admin, usuário novo):
 * mínimo de 10 caracteres, alfanumérica (letras e números), ao menos uma letra maiúscula e
 * ao menos 2 caracteres especiais. Valem só ao DEFINIR; o login aceita qualquer senha já existente.
 */
export const PASSWORD_RULES: readonly PasswordRule[] = [
  {
    id: 'length',
    label: `Mínimo de ${PASSWORD_MIN_LENGTH} caracteres`,
    test: (s) => s.length >= PASSWORD_MIN_LENGTH,
  },
  { id: 'upper', label: 'Ao menos 1 letra maiúscula', test: (s) => /\p{Lu}/u.test(s) },
  { id: 'digit', label: 'Ao menos 1 número', test: (s) => /[0-9]/.test(s) },
  {
    id: 'special',
    label: `Ao menos ${PASSWORD_MIN_SPECIAL} caracteres especiais (ex.: ! @ # $ % &)`,
    test: (s) => countSpecial(s) >= PASSWORD_MIN_SPECIAL,
  },
  { id: 'spaces', label: 'Sem espaços', test: (s) => !/\s/.test(s) },
];

/** Rótulos das regras que a senha NÃO atende (vazio = senha aceita). */
export const passwordIssues = (senha: string): string[] =>
  PASSWORD_RULES.filter((r) => !r.test(senha)).map((r) => r.label);

const senhaSchema = z
  .string()
  .max(200)
  .superRefine((senha, ctx) => {
    const issues = passwordIssues(senha);
    if (issues.length) {
      ctx.addIssue({ code: 'custom', message: `A senha precisa ter: ${issues.join('; ').toLowerCase()}.` });
    }
  });

const PROV_UPPER = 'ABCDEFGHJKLMNPQRSTUVWXYZ';
const PROV_LOWER = 'abcdefghijkmnopqrstuvwxyz';
const PROV_DIGIT = '23456789';
const PROV_SPECIAL = '!@#$%&*?+=';

/**
 * Senha provisória aleatória que já cumpre a política (sem caracteres ambíguos como 0/O e 1/l/I).
 * Usa o gerador criptográfico do ambiente (navegador e Node).
 */
export function gerarSenhaProvisoria(length = 16): string {
  const rng = (globalThis as unknown as { crypto: { getRandomValues<T extends Uint32Array>(a: T): T } })
    .crypto;
  const pick = (set: string) => {
    const buf = rng.getRandomValues(new Uint32Array(1));
    return set[buf[0]! % set.length]!;
  };
  const size = Math.max(length, PASSWORD_MIN_LENGTH);
  const all = PROV_UPPER + PROV_LOWER + PROV_DIGIT;
  // garante as regras e completa com letras e números
  const chars = [
    pick(PROV_UPPER),
    pick(PROV_DIGIT),
    pick(PROV_SPECIAL),
    pick(PROV_SPECIAL),
    pick(PROV_SPECIAL),
  ];
  while (chars.length < size) chars.push(pick(all));
  // embaralha (Fisher–Yates) para os especiais não ficarem sempre no começo
  for (let i = chars.length - 1; i > 0; i--) {
    const j = rng.getRandomValues(new Uint32Array(1))[0]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }
  return chars.join('');
}

// ---------- Sessão ----------

export const loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  senha: z.string().min(1).max(200),
});
export type Login = z.input<typeof loginSchema>;

export const meSchema = z.object({
  login: z.string(),
  nome: z.string(),
  perfil: perfilSchema,
  maquina: optional(z.string()), // operator ligado a uma máquina
  'dois-fatores': z
    .object({
      /** Verificação em duas etapas ativa para este usuário */
      ativo: z.boolean(),
      /** A política exige 2FA para o perfil e o usuário ainda não ativou: só a tela de segurança abre */
      'exige-configuracao': z.boolean(),
    })
    .default({ ativo: false, 'exige-configuracao': false }),
  /** Senha provisória (definida pelo admin ou gerada): só a troca de senha abre até concluir */
  'troca-senha-obrigatoria': z.boolean().default(false),
});
export type Me = z.infer<typeof meSchema>;

// ---------- Usuários (admin) ----------

export const usuarioSchema = z.object({
  id: z.number().int(),
  login: z.string(),
  nome: z.string(),
  perfil: perfilSchema,
  maquina: optional(z.string()),
  ativo: z.boolean(),
  'dois-fatores-ativo': z.boolean().default(false),
  'criado-em': isoDateTimeSchema,
  'ultimo-acesso': optional(isoDateTimeSchema),
});
export type Usuario = z.infer<typeof usuarioSchema>;

const operadorPrecisaMaquina = (u: { perfil?: Perfil; maquina?: string | null }) =>
  u.perfil !== 'operator' || !!u.maquina;
const operadorPrecisaMaquinaIssue = {
  message: 'Operador precisa estar ligado a uma máquina',
  path: ['maquina'],
};

export const usuarioNovoSchema = z
  .object({
    email: z.string().trim().toLowerCase().email(),
    nome: z.string().trim().min(1).max(120),
    perfil: perfilSchema,
    maquina: z.string().trim().min(1).optional(),
    /** Senha provisória: o usuário é obrigado a trocá-la no primeiro acesso */
    senha: senhaSchema,
  })
  .refine(operadorPrecisaMaquina, operadorPrecisaMaquinaIssue);
export type UsuarioNovo = z.input<typeof usuarioNovoSchema>;

export const usuarioAlteracaoSchema = z.object({
  nome: z.string().trim().min(1).max(120).optional(),
  perfil: perfilSchema.optional(),
  maquina: z.string().trim().min(1).nullable().optional(), // null desliga da máquina
  ativo: z.boolean().optional(),
});
export type UsuarioAlteracao = z.input<typeof usuarioAlteracaoSchema>;

/** Admin redefine a senha de outra pessoa; ela é obrigada a trocar no próximo acesso. */
export const redefinirSenhaSchema = z.object({ 'senha-nova': senhaSchema });
export type RedefinirSenha = z.input<typeof redefinirSenhaSchema>;

// ---------- Troca da própria senha ----------

export const trocarSenhaSchema = z
  .object({
    'senha-atual': z.string().min(1).max(200),
    'senha-nova': senhaSchema,
  })
  .refine((v) => v['senha-nova'] !== v['senha-atual'], {
    message: 'A nova senha precisa ser diferente da atual',
    path: ['senha-nova'],
  });
export type TrocarSenha = z.input<typeof trocarSenhaSchema>;

// ---------- Saúde ----------

export const healthSchema = z.object({
  bff: z.literal('ok'),
  /** `mock` quando `DATASUL_MODE=mock` */
  datasul: z.enum(['ok', 'down', 'mock']),
  /** Tempo da checagem do Datasul; `null` em mock */
  'latencia-ms': z.number().nullable(),
  /** `memory` quando o BFF roda sem `DATABASE_URL` (só em desenvolvimento) */
  db: z.enum(['ok', 'down', 'memory']),
  versao: z.string(),
});
export type Health = z.infer<typeof healthSchema>;

// ---------- Verificação em duas etapas (TOTP) ----------

/** Perfis em que a política pode exigir 2FA: todos (decisão de 14/10; por padrão todos são obrigados). */
export const perfilPoliticaSchema = perfilSchema;
export type PerfilPolitica = z.infer<typeof perfilPoliticaSchema>;

/** `POST /auth/login` responde 202 com isto quando falta o segundo fator. */
export const desafioDoisFatoresSchema = z.object({ 'dois-fatores-obrigatorio': z.literal(true) });
export type DesafioDoisFatores = z.infer<typeof desafioDoisFatoresSchema>;

/** Código do app autenticador (6 dígitos) ou código de recuperação (`XXXXX-XXXXX`). */
export const codigoSchema = z.string().trim().min(6).max(20);

export const verificarDoisFatoresSchema = z.object({
  codigo: codigoSchema,
  /** Não pedir o segundo fator neste navegador por 30 dias */
  'confiar-dispositivo': z.boolean().default(false),
});
export type VerificarDoisFatores = z.input<typeof verificarDoisFatoresSchema>;

export const DIAS_DISPOSITIVO_CONFIAVEL = 30;

export const statusDoisFatoresSchema = z.object({
  ativo: z.boolean(),
  'ativo-em': optional(isoDateTimeSchema),
  'codigos-restantes': z.number().int().nonnegative(),
  'dispositivos-confiaveis': z.number().int().nonnegative(),
  /** A política do admin exige 2FA para o perfil deste usuário (não pode desativar) */
  'exigido-por-politica': z.boolean(),
});
export type StatusDoisFatores = z.infer<typeof statusDoisFatoresSchema>;

export const configurarDoisFatoresSchema = z.object({
  /** Segredo em base32, para digitar à mão no app */
  segredo: z.string(),
  /** `otpauth://totp/...` para o QR code */
  'url-otpauth': z.string(),
});
export type ConfigurarDoisFatores = z.infer<typeof configurarDoisFatoresSchema>;

export const ativarDoisFatoresSchema = z.object({ codigo: codigoSchema });
export type AtivarDoisFatores = z.infer<typeof ativarDoisFatoresSchema>;

export const codigosRecuperacaoSchema = z.object({ 'codigos-recuperacao': z.array(z.string()) });
export type CodigosRecuperacao = z.infer<typeof codigosRecuperacaoSchema>;

/** Ações sensíveis (desativar, gerar novos códigos) pedem a senha e um código atual. */
export const reautenticarSchema = z.object({
  senha: z.string().min(1).max(200),
  codigo: codigoSchema,
});
export type Reautenticar = z.infer<typeof reautenticarSchema>;

export const politicaSegurancaSchema = z.object({
  /** Perfis obrigados a usar 2FA. Vazio = opcional para todos (padrão). */
  'perfis-exigem-2fa': z.array(perfilPoliticaSchema),
});
export type PoliticaSeguranca = z.infer<typeof politicaSegurancaSchema>;
